import {
    ActionMenu,
    ButtonPrimary,
    Card,
    Colors,
    defaultHover,
    Heading,
    Icon,
    MultilineInput,
    Pills,
    Spinner,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    type OptionProps,
    type PillsVariant,
} from '@amelio/ui-library';
import { useState } from 'react';

import type { User } from '@/types/directory/User';

import { AvatarStack } from '@/components/AvatarStack/AvatarStack';
import { BuildSurveyQuestionnaireArrowIllustration } from '@/components/Illustrations/Illustration';
import { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';

interface QuestionnaireHeaderProps {
    reviewees: User[];
    previewEvaluee?: User;
    isPendingUpdateQuestionnaire: boolean;
    onPreviewClick?: () => void;
    isEditingTitle: boolean;
    title: string;
    setIsEditingTitle: (value: boolean) => void;
    onCancelTitle: () => void;
    onTitleChange: (title: string) => void;
    isTemplateCreationPage: boolean;
    onSelectTemplateClick?: () => void;
    templateStatus?: ReviewTemplateStatus;
    templateActions?: OptionProps[];
    onTemplateActionClick?: (actionId: string) => void;
}

export const QuestionnaireHeader = ({
    reviewees,
    previewEvaluee,
    isPendingUpdateQuestionnaire,
    onPreviewClick,
    isEditingTitle,
    title,
    setIsEditingTitle,
    onCancelTitle,
    onTitleChange,
    isTemplateCreationPage,
    onSelectTemplateClick,
    templateStatus,
    templateActions,
    onTemplateActionClick,
}: QuestionnaireHeaderProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const readOnlyTitle = replaceDynamicTags({ text: title, employee: previewEvaluee });
    const [originalTitle, setOriginalTitle] = useState<string>(title);

    const handleEditStart = () => {
        setOriginalTitle(title);
        setIsEditingTitle(true);
    };

    const handleCancel = () => {
        onTitleChange(originalTitle);
        onCancelTitle();
    };

    const getStatusVariant = (): PillsVariant => {
        switch (templateStatus) {
            case ReviewTemplateStatus.ACTIVE:
                return 'activeLight';
            case ReviewTemplateStatus.INACTIVE:
                return 'inactiveLight';
            case ReviewTemplateStatus.DRAFT:
                return 'draftLight';
            default:
                return 'default';
        }
    };

    const getStatusText = (): string => {
        switch (templateStatus) {
            case ReviewTemplateStatus.ACTIVE:
                return t('common.active');
            case ReviewTemplateStatus.INACTIVE:
                return t('common.inactive');
            case ReviewTemplateStatus.DRAFT:
                return t('common.draft');
            default:
                return '';
        }
    };

    return (
        <>
            <div className={'relative overflow-visible'}>
                <Card
                    className={'max-w-[500px] flex-col items-center gap-2 px-12 py-8 md:flex-row'}
                    borderRadius={'2xl'}
                >
                    <Heading
                        level={3}
                        weight={'medium'}
                        className={'shrink-0 md:w-[220px] md:pr-5'}
                    >
                        {t('evaluation360Questionnaire.header.title')}
                    </Heading>

                    <Text
                        className={'mt-1'}
                        color={Colors.BASE_600}
                    >
                        {t('evaluation360Questionnaire.header.description')}
                    </Text>
                </Card>

                <BuildSurveyQuestionnaireArrowIllustration
                    className={
                        'pointer-events-none absolute top-15 left-[400px] z-2 !hidden rotate-30 lg:!block'
                    }
                />
            </div>

            <div
                className={
                    'mt-10 flex flex-wrap justify-between gap-4 md:!mt-20 md:justify-start md:gap-0'
                }
            >
                <div className={'flex items-center gap-4'}>
                    <AvatarStack
                        users={reviewees}
                        maxVisible={5}
                        size={'2xs'}
                        spacing={'loose'}
                        showBorder={true}
                        borderSize={'ring-2'}
                        rtl={false}
                        showTooltip={true}
                    />

                    {templateStatus && (
                        <div className={'flex items-center gap-2 whitespace-nowrap'}>
                            <Text className={'text-sm font-medium'}>
                                {t('common.templateStatus')}:
                            </Text>
                            <Pills variant={getStatusVariant()}>{getStatusText()}</Pills>
                        </div>
                    )}
                </div>

                <div className={'ml-auto flex flex-wrap items-center justify-end gap-4'}>
                    {onSelectTemplateClick && !isTemplateCreationPage && (
                        <ButtonPrimary
                            size={'md'}
                            className={
                                'lg:max-2xl border-teal text-primary z-10 border bg-white/30'
                            }
                            onClick={onSelectTemplateClick}
                            dataTestId={'select-new-template-button'}
                        >
                            {t('evaluation360Questionnaire.header.selectNewTemplate')}
                        </ButtonPrimary>
                    )}

                    <ButtonPrimary
                        size={'md'}
                        className={'border-teal text-primary z-10 border bg-white/30 md:min-w-10'}
                        onClick={onPreviewClick}
                    >
                        {t('evaluation360Questionnaire.header.preview')}
                    </ButtonPrimary>

                    {templateActions && templateActions.length > 0 && (
                        <ActionMenu
                            items={templateActions}
                            onItemClick={onTemplateActionClick}
                            className={'!h-[40px] !w-[40px]'}
                        />
                    )}
                </div>
            </div>

            <Card
                padding={'lg'}
                borderRadius={'2xl'}
                className={'w-full min-w-0 overflow-hidden'}
            >
                <div
                    className={
                        'grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-4'
                    }
                >
                    {isEditingTitle ? (
                        <>
                            <div className={'min-w-0'}>
                                <MultilineInput
                                    placeholder={'What topics would you need more information on?'}
                                    value={title}
                                    onChange={onTitleChange}
                                    size={'lg'}
                                    className={
                                        'z-3 border border-white bg-white/70 !text-[21px] font-medium focus-within:border-2 focus-within:border-teal-400'
                                    }
                                    autoGrow={true}
                                    rows={1}
                                    variant={'white'}
                                />
                            </div>

                            {isPendingUpdateQuestionnaire ? (
                                <Spinner
                                    size={'xs'}
                                    color={Colors.PRIMARY_600}
                                />
                            ) : (
                                <div className={'flex gap-3'}>
                                    <button
                                        onClick={() => setIsEditingTitle(false)}
                                        className={
                                            'rounded rounded-lg border border-teal-400 bg-white px-3 py-1 text-sm font-medium text-teal-400 hover:bg-teal-50'
                                        }
                                    >
                                        {t('common.save')}
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        className={
                                            'rounded rounded-lg border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-500 hover:bg-gray-50'
                                        }
                                    >
                                        {t('common.cancel')}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className={'flex h-18 min-w-0 items-center pe-3'}>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className={'flex min-w-0 items-center gap-2'}>
                                                <Heading
                                                    level={3}
                                                    weight={'medium'}
                                                    className={
                                                        'block min-w-0 cursor-pointer overflow-hidden p-5 text-ellipsis whitespace-nowrap'
                                                    }
                                                >
                                                    {readOnlyTitle}
                                                </Heading>
                                                <Icon
                                                    name={'money-icon_pencil'}
                                                    onClick={handleEditStart}
                                                    size={'lg'}
                                                    className={`shrink-0 cursor-pointer ${defaultHover}`}
                                                />
                                            </div>
                                        </TooltipTrigger>

                                        <TooltipContent
                                            side={'top'}
                                            align={'end'}
                                            className={'max-w-lg break-words whitespace-normal'}
                                        >
                                            {readOnlyTitle}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </>
                    )}
                </div>
            </Card>
        </>
    );
};
